import React, { useContext, useEffect, useMemo, useState } from 'react'
import UserContext from '../contexts/UserContext';
import { useTranslation } from 'react-i18next';
import useFetch from '../hooks/useFetch';
import { getAllMyPayments, getCurrentSubscriptionPrices, getMyBillingInfo } from '../config/apiRoutes';
import { BarionPaymentStatus, deserializeSubscriptionPayment, SerializedSubscriptionPayment, SubscriptionPayment } from '../models/SubscriptionPayment';
import { SubscriptionPlanPrice } from '../models/SubscriptionPlanPrice';
import { BillingoPartner } from '../models/BillingoPartner';
import BillingoPartnerEditor from '../billing/BillingoPartnerEditor';
import InvoiceTable from '../billing/InvoiceTable';
import BillingoPartnerPaper from '../billing/BillingoPartnerPaper';
import CardInfo from '../payments/CardInfo';
import CurrentSubscriptionCard from '../subscription/CurrentSubscriptionCard';
import PageContainer from '../containers/PageContainer';
import PageTitleCard from '../cards/PageTitleCard';
import SectionTitle from '../texts/SectionTitle';
import ResponsiveModal from '../modal/ResponsiveModal';
import PageTitleText from '../texts/PageTitleText';

type Props = {}

const PaymentSettingsPage = (props: Props) => {
  const { currentSubscription, selectedRestaurant } = useContext(UserContext);
  const paymentCaller = useFetch();
  const priceCaller = useFetch();
  const billingInfoCaler = useFetch();
  const { t } = useTranslation();
  const [payments, setPayments] = useState<SubscriptionPayment[]>([]);
  const successfulPayments = useMemo(() => {
    return payments.filter(payment => payment.status === BarionPaymentStatus.Succeded);
  }, [payments]);
  const [prices, setPrices] = useState<SubscriptionPlanPrice[]>([]);
  const [billingoPartner, setBillingoPartner] = useState<BillingoPartner | null>(null);
  const [billingInfoEditorOpen, setBillingInfoEditorOpen] = useState(false);
  const currentRecurrence = useMemo(() => {
    if (currentSubscription == null || prices.length === 0) return undefined;
    return payments.filter(payment =>
      payment.paymentRecurrence != null
      && payment.paymentRecurrence.subscriptionPrice != null
      && payment.paymentRecurrence.subscriptionPrice.planId === currentSubscription.subscriptionPlanId
      && payment.paymentRecurrence.subscriptionPrice.interval === currentSubscription.paymentInterval
    ).map(payment => payment.paymentRecurrence)
      .find(recurrence => recurrence?.isActive);
  }, [payments, currentSubscription]);

  useEffect(() => {
    document.title = `QuickPick - ${t("navigation.payment_settings")}`;
  }, [t("navigation.payment_settings")]);

  React.useEffect(() => {
    fetchPayments();
    fetchMyBillingInfo();
  }, [selectedRestaurant]);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = '/scripts/barion.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  async function fetchMyBillingInfo() {
    if (selectedRestaurant == null) return;
    const url = getMyBillingInfo.replace(":restaurantShortName", selectedRestaurant.restaurantShortName);
    const response = await billingInfoCaler.call<any, BillingoPartner | null>("GET", url);
    if (response.ok && response.data) {
      setBillingoPartner(response.data);
    } else {
      setBillingoPartner(null);
    }
  }

  async function fetchPayments() {
    if (selectedRestaurant == null) return;
    const url = getAllMyPayments.replace(":restaurantShortName", selectedRestaurant.restaurantShortName);
    const response = await paymentCaller.call<any, SerializedSubscriptionPayment[]>("GET", url);
    if (response.ok && response.data) {
      setPayments(response.data.map(payment => deserializeSubscriptionPayment(payment)));
    }
  }

  async function fetchSubscriptionPlans() {
    const response = await priceCaller.call<any, SubscriptionPlanPrice[]>("GET", getCurrentSubscriptionPrices);
    if (response.ok && response.data) {
      setPrices(response.data);
    }
  }

  function handleBillingInfoEditClick() {
    setBillingInfoEditorOpen(true);
  }

  function onBillingoPartnerSaved(partner: BillingoPartner) {
    setBillingoPartner(partner);
    setBillingInfoEditorOpen(false);
  }

  return (
    <>
      <PageContainer>
        <PageTitleCard>
          <PageTitleText>
            {t("navigation.payment_settings")}
          </PageTitleText>
        </PageTitleCard>
        <CurrentSubscriptionCard
          prices={prices}
        />
        {currentRecurrence != null && currentSubscription && currentSubscription.status === "ACTIVE" && (
          <div className="mt-5">
            <SectionTitle>
              {t("payment.payment_method")}
            </SectionTitle>
            <div>
              {currentSubscription != null && (
                <CardInfo
                  paymentRecurrence={currentRecurrence}
                  currentSubscription={currentSubscription}
                />
              )}
            </div>
          </div>
        )}
        {billingoPartner && (
          <div className="mt-5">
            <SectionTitle>
              {t("payment.billing_information")}
            </SectionTitle>
            <div>
              <BillingoPartnerPaper
                billingoPartner={billingoPartner}
                onEditClick={handleBillingInfoEditClick}
              />
            </div>
          </div>
        )}
        <div className="mt-5 mb-5">
          <SectionTitle>
            {t("payment.payments_and_invoices")}
          </SectionTitle>
          <div>
            <InvoiceTable
              payments={successfulPayments}
            />
            {successfulPayments.length === 0 && !paymentCaller.loading && (
              <div className="text-center text-gray-500 mt-4">
                {t("payment.no_available_invoices")}
              </div>
            )}
          </div>
        </div>
      </PageContainer>
      <ResponsiveModal
        open={billingInfoEditorOpen} // This should be controlled by a state variable
        handleClose={() => { setBillingInfoEditorOpen(false) }} // This should handle closing the modal
      >
        <>
          {billingoPartner && (
            <BillingoPartnerEditor
              billingoPartner={billingoPartner}
              onSave={onBillingoPartnerSaved}
            />
          )}
        </>
      </ResponsiveModal>
    </>
  )
}

export default PaymentSettingsPage